import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { ClipboardCheck, Clock, LogOut, Shield } from "lucide-react";

/**
 * Forces employees (not admins) to check in before they can use the app today.
 */
export default function AttendanceGate({ children }) {
  const { user, logout } = useAuth();
  const [checking, setChecking] = useState(true);
  const [needsCheckin, setNeedsCheckin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(new Date());

  const refresh = async () => {
    if (!user || user.role !== "employee") {
      setNeedsCheckin(false);
      setChecking(false);
      return;
    }
    try {
      const { data } = await api.get("/attendance/me");
      const checkedIn = data?.today?.check_in;
      setNeedsCheckin(!checkedIn);
    } catch {
      setNeedsCheckin(false);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const checkin = async () => {
    setBusy(true);
    try {
      await api.post("/attendance/checkin");
      toast.success(`Welcome ${user.name.split(" ")[0]}! Have a productive day.`);
      setNeedsCheckin(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Check-in failed");
    } finally { setBusy(false); }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="label-uppercase">Loading…</div>
      </div>
    );
  }

  if (!needsCheckin) return children;

  // Full-screen check-in gate (employees only)
  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4" data-testid="attendance-gate">
      <div className="w-full max-w-md border border-zinc-300">
        <div className="bg-amber-50 border-b border-amber-300 px-6 py-3 flex items-center gap-2">
          <Shield size={14} className="text-amber-700" />
          <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-amber-800">Attendance Required</span>
        </div>
        <div className="px-8 py-10 text-center">
          <div className="w-16 h-16 mx-auto mb-5 bg-zinc-900 text-white flex items-center justify-center">
            <ClipboardCheck size={28} strokeWidth={1.5} />
          </div>
          <div className="label-uppercase mb-2">Good morning, {user.name.split(" ")[0]}</div>
          <h1 className="text-3xl font-bold tracking-tighter mb-3" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
            Mark your attendance
          </h1>
          <p className="text-sm text-zinc-600 leading-relaxed mb-6">
            Please check in for the day before continuing. You can't access tasks, leads or any other module until your attendance is recorded.
          </p>

          <div className="flex items-center justify-center gap-3 mb-8">
            <Clock size={18} className="text-zinc-400" />
            <div className="text-3xl font-bold font-mono">
              {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </div>
          </div>

          <button
            onClick={checkin}
            disabled={busy}
            data-testid="gate-checkin-button"
            className="w-full bg-ikb text-white py-3 text-sm font-medium tracking-wide hover:bg-[#00227A] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <ClipboardCheck size={16} />
            {busy ? "Checking in…" : "Check In Now"}
          </button>

          <button
            onClick={() => { logout(); window.location.href = "/login"; }}
            data-testid="gate-logout-button"
            className="w-full mt-3 text-xs uppercase tracking-wider text-zinc-500 hover:text-zinc-900 py-2 flex items-center justify-center gap-2"
          >
            <LogOut size={11} /> Sign out instead
          </button>
        </div>
        <div className="border-t border-zinc-200 px-6 py-3 bg-zinc-50">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 text-center">
            {now.toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </div>
        </div>
      </div>
    </div>
  );
}
